import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatMoneyCHF } from '../utils/priceUtils';
import {
  FaSearch, FaTimes, FaCheck, FaBuilding, FaSpinner, FaPlus, FaTrash, FaBoxes,
} from 'react-icons/fa';
import {
  searchProducts,
  searchCompanies,
  fetchProductsByCompany,
  addMultipleBestsellers,
} from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Bulk Bestseller Modal — admin bestseller manager
 *
 * Same search UX as BulkDiscountModal: typeahead by name/pharmacode,
 * optional company filter, scrollable multi-select results, dedup by
 * pharmacode (defensive — the search endpoint already collapses
 * duplicates server-side).
 *
 * Submit calls `POST /addProductToBestseller/<csv-pharmacodes>` via the
 * existing `addMultipleBestsellers` helper. The backend already separates
 * outcomes into successful / alreadyExists / failed buckets and returns a
 * detailed message; we render that verbatim along with per-bucket counts.
 */
const BulkBestsellerModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();

  // ── Selection panel state ──────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Map<pharmacode, { pharmacode, name, basePrice, company }>
  const [selected, setSelected] = useState(new Map());

  // ── Submission state ───────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);

  // Reset everything when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setCompanyFilter('');
    setCompanyQuery('');
    setSearchResults([]);
    setSelected(new Map());
    setError(null);
    setSubmitResult(null);
  }, [isOpen]);

  // Company typeahead — only kicks in after 2 chars.
  useEffect(() => {
    if (!isOpen) return;
    if (!companyQuery.trim() || companyQuery.trim().length < 2) {
      setCompanies([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await searchCompanies(companyQuery.trim(), 10);
        setCompanies(r?.data?.companies || r?.companies || []);
      } catch { setCompanies([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [companyQuery, isOpen]);

  // Debounced product search.
  useEffect(() => {
    if (!isOpen) {
      setSearchResults([]);
      return;
    }
    const term = searchTerm.trim();
    const hasFilters = term.length >= 2 || !!companyFilter;
    if (!hasFilters) { setSearchResults([]); return; }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = companyFilter
          ? await fetchProductsByCompany(companyFilter, 1, 50)
          : await searchProducts(term, 1, 50);
        setSearchResults(response?.data?.products || []);
      } catch { setSearchResults([]); }
      finally { setIsSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, companyFilter, isOpen]);

  // Normalize a result product into a row: the first displayable article.
  // Dedup by pharmacode defensively — search endpoint already collapses
  // cross-hit duplicates, but keeping a Map here makes the modal robust
  // against any future regression.
  const rowsFromResults = useMemo(() => {
    const byCode = new Map();
    for (const p of searchResults) {
      const article = p.articles?.[0];
      if (!article || !article.pharmacode) continue;
      if (byCode.has(article.pharmacode)) continue;
      const name =
        article.description?.longDescription ||
        article.description?.description ||
        article.longDescription ||
        `Pharmacode ${article.pharmacode}`;
      const company = Array.isArray(p.companies) && p.companies[0]?.name
        ? p.companies[0].name
        : null;
      const basePrice =
        article.price?.basePrice ??
        article.basePrice ??
        article.publicPrice?.price ??
        null;
      byCode.set(article.pharmacode, {
        pharmacode: article.pharmacode,
        name,
        company,
        basePrice,
        hasPhoto: !!article.hasPhoto,
      });
    }
    return Array.from(byCode.values());
  }, [searchResults]);

  const toggleSelect = useCallback((row) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.pharmacode)) next.delete(row.pharmacode);
      else next.set(row.pharmacode, row);
      return next;
    });
  }, []);

  const removeSelected = useCallback((pharmacode) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(pharmacode);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of rowsFromResults) if (!next.has(r.pharmacode)) next.set(r.pharmacode, r);
      return next;
    });
  }, [rowsFromResults]);

  const deselectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of rowsFromResults) next.delete(r.pharmacode);
      return next;
    });
  }, [rowsFromResults]);

  const handleSubmit = async () => {
    setError(null);
    const codes = Array.from(selected.keys());
    if (codes.length === 0) {
      setError(t('bestsellers.modals.bulkAdd.errors.selectAtLeastOne') || 'Select at least one product to add.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await addMultipleBestsellers(codes);
      setSubmitResult(response);
      // The backend returns 400 if zero succeed — axios throws on that path,
      // so we'll only land here when success is true (i.e. ≥ 1 was added).
      if (response.success) {
        onSuccess?.(response);
      }
    } catch (e) {
      const data = e.response?.data;
      // Surface the detailed backend message verbatim — it now contains
      // the breakdown ("3 added, 1 already in bestsellers, 1 not found").
      setError(data?.message || data?.error || e.message || 'Request failed.');
      // Keep the result object too so the user can see per-bucket counts.
      if (data) setSubmitResult(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          className="w-full max-w-5xl max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.18 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md">
                <FaBoxes className="text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {t('bestsellers.modals.bulkAdd.title') || 'Add bestsellers'}
                </h2>
                <p className="text-sm text-gray-600">
                  {t('bestsellers.modals.bulkAdd.subtitle') ||
                    'Search products and select multiple to add to the bestseller list at once.'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body: two columns */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-0 overflow-hidden">
            {/* ── Left: search + results ─────────────────────────── */}
            <div className="lg:col-span-3 flex flex-col border-r border-gray-200 min-h-0">
              <div className="p-5 space-y-3 bg-gray-50/60 border-b border-gray-200">
                {/* Search bar */}
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('bestsellers.modals.bulkAdd.searchPlaceholder') ||
                      'Search by name, brand, pharmacode…'}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                </div>

                {/* Company filter */}
                <div className="relative">
                  <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={companyFilter ? companyFilter : companyQuery}
                    onChange={(e) => {
                      setCompanyQuery(e.target.value);
                      setCompanyFilter('');
                      setShowCompanyDropdown(true);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 150)}
                    placeholder={t('bestsellers.modals.bulkAdd.companyPlaceholder') ||
                      'Filter by company / brand (optional)'}
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                  {(companyFilter || companyQuery) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyFilter('');
                        setCompanyQuery('');
                        setCompanies([]);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear company filter"
                    >
                      <FaTimes />
                    </button>
                  )}
                  {showCompanyDropdown && companies.length > 0 && !companyFilter && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                      {companies.map((c, i) => {
                        const name = typeof c === 'string' ? c : (c.name || c.companyName || '');
                        if (!name) return null;
                        return (
                          <button
                            key={`${name}-${i}`}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setCompanyFilter(name);
                              setCompanyQuery('');
                              setShowCompanyDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          >
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Active filter chips */}
                {companyFilter && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium">
                      <FaBuilding className="w-3 h-3" />
                      {companyFilter}
                      <button
                        onClick={() => setCompanyFilter('')}
                        className="ml-1 hover:text-indigo-900"
                        aria-label="Remove company filter"
                      >
                        <FaTimes className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  </div>
                )}
              </div>

              {/* Results header with bulk-select controls */}
              <div className="flex items-center justify-between px-5 py-2 bg-white border-b border-gray-100 text-xs text-gray-500">
                <span>
                  {isSearching ? (
                    <span className="inline-flex items-center gap-1.5">
                      <FaSpinner className="animate-spin" />{' '}
                      {t('bestsellers.modals.bulkAdd.searching') || 'Searching…'}
                    </span>
                  ) : rowsFromResults.length > 0 ? (
                    `${rowsFromResults.length} ${
                      rowsFromResults.length === 1
                        ? (t('bestsellers.modals.bulkAdd.result') || 'result')
                        : (t('bestsellers.modals.bulkAdd.results') || 'results')
                    }`
                  ) : (
                    t('bestsellers.modals.bulkAdd.noResultsYet') || 'No results yet'
                  )}
                </span>
                {rowsFromResults.length > 0 && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={selectAllVisible}
                      className="text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      {t('bestsellers.modals.bulkAdd.selectAllVisible') || 'Select all visible'}
                    </button>
                    <span className="text-gray-300">·</span>
                    <button
                      type="button"
                      onClick={deselectAllVisible}
                      className="text-gray-600 hover:text-gray-800 font-medium"
                    >
                      {t('bestsellers.modals.bulkAdd.deselectVisible') || 'Deselect visible'}
                    </button>
                  </div>
                )}
              </div>

              {/* Scrollable results list */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {rowsFromResults.length === 0 && !isSearching && (
                  <div className="p-12 text-center text-gray-500">
                    <FaSearch className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">
                      {t('bestsellers.modals.bulkAdd.startTyping') || 'Start typing or pick a company'}
                    </p>
                    <p className="text-sm mt-1">
                      {t('bestsellers.modals.bulkAdd.resultsHint') ||
                        'Results will appear here. Click a row to select it.'}
                    </p>
                  </div>
                )}
                {rowsFromResults.map((row) => {
                  const isSel = selected.has(row.pharmacode);
                  return (
                    <button
                      key={row.pharmacode}
                      type="button"
                      onClick={() => toggleSelect(row)}
                      className={`w-full text-left flex items-center gap-3 px-5 py-3 border-b border-gray-100 transition-colors ${
                        isSel
                          ? 'bg-indigo-50/70 hover:bg-indigo-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSel
                            ? 'bg-indigo-500 border-indigo-500 text-white'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSel && <FaCheck className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{row.name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                          <span>#{row.pharmacode}</span>
                          {row.company && <><span>·</span><span className="truncate">{row.company}</span></>}
                        </div>
                      </div>
                      {row.basePrice != null && (
                        <div className="text-right text-sm text-gray-700 font-medium tabular-nums whitespace-nowrap">
                          {formatMoneyCHF(row.basePrice)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Right: selected list + status ────────── */}
            <div className="lg:col-span-2 flex flex-col bg-gradient-to-b from-white to-gray-50 min-h-0">
              <div className="px-5 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">
                    {t('bestsellers.modals.bulkAdd.selected') || 'Selected'}{' '}
                    <span className="text-indigo-600">({selected.size})</span>
                  </h3>
                  {selected.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelected(new Map())}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      {t('bestsellers.modals.bulkAdd.clearAll') || 'Clear all'}
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto -mx-1">
                  {selected.size === 0 ? (
                    <p className="text-sm text-gray-400 italic px-1 py-2">
                      {t('bestsellers.modals.bulkAdd.noneSelected') || 'No products selected yet.'}
                    </p>
                  ) : (
                    Array.from(selected.values()).map((row) => (
                      <div
                        key={row.pharmacode}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white text-sm"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-gray-800">{row.name}</div>
                          <div className="text-xs text-gray-500">
                            #{row.pharmacode}
                            {row.basePrice != null && <> · {formatMoneyCHF(row.basePrice)}</>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSelected(row.pharmacode)}
                          className="text-gray-400 hover:text-red-600 p-1"
                          aria-label="Remove"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Result / error region */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {submitResult?.success && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-sm">
                    {submitResult.message ||
                      `Added ${submitResult.data?.summary?.successCount ?? selected.size} product(s) to bestsellers.`}
                    {submitResult.data?.summary?.alreadyExistsCount > 0 && (
                      <div className="mt-1 text-xs text-amber-700">
                        {submitResult.data.summary.alreadyExistsCount} were already in bestsellers and were skipped.
                      </div>
                    )}
                  </div>
                )}

                {submitResult?.data?.summary && (
                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-3 space-y-1.5 text-sm">
                    <div className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-1">
                      {t('bestsellers.modals.bulkAdd.breakdown') || 'Breakdown'}
                    </div>
                    <div className="flex justify-between text-green-700">
                      <span>Added</span>
                      <span className="font-medium tabular-nums">{submitResult.data.summary.successCount || 0}</span>
                    </div>
                    {submitResult.data.summary.alreadyExistsCount > 0 && (
                      <div className="flex justify-between text-amber-700">
                        <span>Already in bestsellers</span>
                        <span className="font-medium tabular-nums">{submitResult.data.summary.alreadyExistsCount}</span>
                      </div>
                    )}
                    {submitResult.data.summary.failedCount > 0 && (
                      <div className="flex justify-between text-red-700">
                        <span>Failed</span>
                        <span className="font-medium tabular-nums">{submitResult.data.summary.failedCount}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Per-row failure reasons (helps the admin debug missing pharmacodes) */}
                {Array.isArray(submitResult?.data?.failed) && submitResult.data.failed.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 space-y-1">
                    <div className="font-semibold uppercase tracking-wide mb-1">
                      {t('bestsellers.modals.bulkAdd.failedDetails') || 'Failed details'}
                    </div>
                    {submitResult.data.failed.slice(0, 8).map((f) => (
                      <div key={f.pharmacode} className="flex gap-2">
                        <span className="font-mono">#{f.pharmacode}</span>
                        <span className="flex-1">— {f.error}</span>
                      </div>
                    ))}
                    {submitResult.data.failed.length > 8 && (
                      <div className="text-red-600 italic">…and {submitResult.data.failed.length - 8} more.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="text-sm text-gray-600">
              {selected.size === 0
                ? (t('bestsellers.modals.bulkAdd.noneSelectedShort') || 'No products selected')
                : `${selected.size} ${selected.size === 1 ? 'product' : 'products'} selected`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-xl border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {submitResult?.success
                  ? (t('bestsellers.modals.bulkAdd.close') || 'Close')
                  : (t('bestsellers.modals.bulkAdd.cancel') || 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || selected.size === 0}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold shadow-md hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    {t('bestsellers.modals.bulkAdd.adding') || 'Adding…'}
                  </>
                ) : (
                  <>
                    <FaPlus />
                    {t('bestsellers.modals.bulkAdd.addCount', { count: selected.size || 0 }) ||
                      `Add ${selected.size || 0} to bestsellers`}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BulkBestsellerModal;
